/**
 * Utility functions shared across the application
 * @module utils
 */

/**
 * Detects if the current device is mobile based on user agent and viewport width
 * @returns {boolean} True if mobile device detected
 */
export function isMobileDevice() {
  const mobileUserAgentPattern = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUserAgent = mobileUserAgentPattern.test(navigator.userAgent);
  const isSmallViewport = window.innerWidth <= 768;

  return isMobileUserAgent || isSmallViewport;
}

/**
 * Updates the time display element with current time (HH:MM format)
 * @param {string} elementId - The ID of the element to update
 * @returns {string} The formatted time string
 */
export function updateTimeDisplay(elementId) {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const timeString = `${hours}:${minutes}`;

  const element = document.getElementById(elementId);
  if (element) {
    element.textContent = timeString;
  }

  return timeString;
}

/**
 * Starts an interval to update time display every minute
 * @param {string} elementId - The ID of the element to update
 * @returns {number} The interval ID for cleanup
 */
export function startTimeUpdater(elementId) {
  updateTimeDisplay(elementId);
  return setInterval(() => updateTimeDisplay(elementId), 60000);
}

/**
 * Redirects to a URL based on device type
 * @param {string} mobileUrl - URL for mobile devices
 * @param {string} desktopUrl - URL for desktop devices
 */
export function redirectByDevice(mobileUrl, desktopUrl) {
  const targetUrl = isMobileDevice() ? mobileUrl : desktopUrl;
  window.location.href = targetUrl;
}

/**
 * Debounce function to limit rapid function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
